import React, { useEffect, useState } from "react";
import { Activity, CalendarDays, Check, ChevronRight, Link2, Loader2, Wand2 } from "lucide-react";
import { Button } from "../ui/button";
import DetailSheet from "../session/generate/DetailSheet";
import { Group, ItemRow } from "../profile/ProfileParts";
import { cn } from "../../lib/utils";
import { getSessions } from "../../services/api";
import { slotIdentity } from "../../config/periodisation";
import { adaptationsForWorkoutType, readCountsAs } from "../../utils/trainingBalance";
import {
  currentProgrammeWeek,
  slotName,
  slotWeekday,
  weekDayDate,
  weekdayOf,
} from "../../utils/programmeSchedule";

// The week as a pool of sessions (#196): any session not done yet can go on
// another day of its week (rest days and doubling up included), back into
// the week's pool, or be skipped. Sessions stay within their week, so the
// deloads keep their timing.

const DAY_LETTERS = ["M", "T", "W", "T", "F", "S", "S"];
const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const isMovable = (session) =>
  session?.status === "pending" || session?.status === "skipped";

const weekOf = (programme, weekNumber) =>
  (programme?.weeks || []).find((w) => w.week_number === weekNumber) || { sessions: [] };

/** Weekdays already gone in this programme week (none in a future week). */
const pastDays = (programme, weekNumber) => {
  const current = currentProgrammeWeek(programme);
  if (weekNumber < current) return 7;
  if (weekNumber > current) return 0;
  return weekdayOf(new Date());
};

const dayText = (programme, session) => {
  if (session.status === "skipped") return "Skipped this week";
  const weekday = slotWeekday(programme, session);
  if (weekday == null) return "Not on a day yet";
  const date = weekDayDate(programme, session.week_number, weekday);
  return date
    ? date.toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })
    : DAY_SHORT[weekday];
};

/**
 * Where a session goes: a day of its week (past days greyed out), back to
 * the pool, or skipped. A dot marks a day that already has a session.
 */
export function MoveSessionSheet({ open, onOpenChange, programme, session, onPlace, onSkip, onBack, onCountDone }) {
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!session) return null;
  const weekNumber = session.week_number;
  const gone = pastDays(programme, weekNumber);
  const current = slotWeekday(programme, session);
  const others = weekOf(programme, weekNumber).sessions.filter(
    (s) => s.id !== session.id && s.status !== "skipped",
  );
  const taken = new Set(others.map((s) => slotWeekday(programme, s)).filter((d) => d != null));
  const choice = picked ?? current;

  const run = (fn) => async () => {
    setBusy(true);
    try {
      await fn();
      onOpenChange(false);
    } finally {
      setBusy(false);
      setPicked(null);
    }
  };

  return (
    <DetailSheet
      open={open}
      onOpenChange={(next) => {
        if (!next) setPicked(null);
        onOpenChange(next);
      }}
      onBack={onBack}
      title={slotName(session)}
      description={`Now: ${dayText(programme, session)}`}
      showDescription
      footer={
        <Button
          type="button"
          className="h-12 w-full text-base font-bold"
          disabled={busy || choice == null || (choice === current && session.status !== "skipped")}
          onClick={run(() => onPlace(session, choice))}
        >
          {choice == null
            ? "Pick a day"
            : `Put on ${DAY_SHORT[choice]} ${weekDayDate(programme, weekNumber, choice)?.getDate() ?? ""}`.trim()}
        </Button>
      }
    >
      <div className="grid gap-1.5">
        <p className="text-xs font-semibold text-muted-foreground">Which day?</p>
        <div role="radiogroup" aria-label="Day" className="grid grid-cols-7 gap-1.5">
          {DAY_LETTERS.map((letter, d) => {
            const date = weekDayDate(programme, weekNumber, d);
            const past = d < gone;
            return (
              <button
                key={d}
                type="button"
                role="radio"
                aria-checked={choice === d}
                aria-label={`${DAY_SHORT[d]} ${date?.getDate() ?? ""}${taken.has(d) ? ", has a session" : ""}`}
                disabled={past}
                onClick={() => setPicked(d)}
                className={cn(
                  "grid min-h-[52px] place-items-center rounded-xl border py-1.5 text-xs leading-tight transition-colors",
                  choice === d
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border bg-background text-muted-foreground hover:border-primary/40",
                  past && "opacity-35",
                )}
              >
                <span className={cn("text-sm font-semibold", choice === d ? "text-primary" : "text-foreground")}>
                  {letter}
                </span>
                <span className="tabular-nums">{date?.getDate()}</span>
                <span className={cn("h-1 w-1 rounded-full", taken.has(d) ? "bg-muted-foreground" : "bg-transparent")} />
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">A dot means a session is already there; you'd do both that day.</p>
      </div>
      <Group title="Or">
        {(current != null || session.status === "skipped") && (
          <ItemRow
            Icon={CalendarDays}
            title="Back to the pool"
            detail="Place it later this week"
            disabled={busy}
            onClick={run(() => onPlace(session, null))}
          />
        )}
        {onCountDone && (
          <ItemRow
            Icon={Link2}
            title="I've done it already"
            detail="Count a session from your history"
            disabled={busy}
            onClick={() => onCountDone(session)}
          />
        )}
        {session.status === "pending" && (
          <ItemRow
            Icon={ChevronRight}
            title="Skip this week"
            detail="Counts as skipped, not missed"
            disabled={busy}
            onClick={run(() => onSkip(session))}
          />
        )}
      </Group>
    </DetailSheet>
  );
}

/** A session as a row in the week: name, and where it is. */
const SessionChip = ({ programme, session, onClick }) => {
  const movable = isMovable(session);
  return (
    <button
      type="button"
      onClick={movable ? onClick : undefined}
      disabled={!movable}
      className={cn(
        "flex min-w-0 items-center justify-between gap-2 rounded-xl border border-border bg-card px-3 py-2 text-left transition-colors",
        movable ? "hover:border-primary/40" : "cursor-default",
        session.status === "completed" && "opacity-60",
        (session.status === "skipped" || session.status === "missed") && "opacity-50",
      )}
    >
      <span
        className={cn(
          "min-w-0 truncate text-sm font-semibold",
          session.status === "skipped" && "line-through",
        )}
      >
        {session.status === "completed" ? `${"✓"} ` : ""}
        {slotName(session)}
      </span>
      <span className="shrink-0 text-xs text-muted-foreground">
        {session.status === "completed"
          ? "Done"
          : session.status === "missed"
            ? "Missed"
            : session.status === "skipped"
              ? "Skipped"
              : "Move"}
      </span>
    </button>
  );
};

/**
 * Plan a week: the pool of sessions not on a day, then the seven days with
 * what's on each. Tap a session to move it.
 */
export function WeekPlanSheet({ open, onOpenChange, programme, weekNumber, onPlace, onSkip, onLink }) {
  const [moving, setMoving] = useState(null);
  const [linking, setLinking] = useState(null);
  const week = weekOf(programme, weekNumber);
  const sessions = week.sessions || [];
  const pool = sessions.filter((s) => s.status !== "skipped" && slotWeekday(programme, { ...s, week_number: weekNumber }) == null && s.status === "pending");
  const skipped = sessions.filter((s) => s.status === "skipped");
  const done = sessions.filter((s) => s.status === "completed").length;
  const gone = pastDays(programme, weekNumber);
  const today = currentProgrammeWeek(programme) === weekNumber ? weekdayOf(new Date()) : null;
  const withWeek = (s) => ({ ...s, week_number: s.week_number ?? weekNumber });
  const open2 = (s) => setMoving(withWeek(s));

  return (
    <>
      <DetailSheet
        open={open && !moving && !linking}
        onOpenChange={onOpenChange}
        title={`Plan week ${weekNumber}`}
        description={`${done} of ${sessions.length} done`}
        showDescription
      >
        {(pool.length > 0 || skipped.length > 0) && (
          <div className="grid gap-1.5 rounded-xl border border-dashed border-white/20 bg-white/[0.02] p-2.5">
            <p className="flex justify-between text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Not on a day yet</span>
              <span>tap to place</span>
            </p>
            {[...pool, ...skipped].map((s) => (
              <SessionChip key={s.id} programme={programme} session={s} onClick={() => open2(s)} />
            ))}
          </div>
        )}
        <div className="divide-y divide-border">
          {DAY_SHORT.map((name, d) => {
            const here = sessions.filter(
              (s) => s.status !== "skipped" && slotWeekday(programme, withWeek(s)) === d,
            );
            const date = weekDayDate(programme, weekNumber, d);
            return (
              <div key={d} className={cn("grid grid-cols-[3rem_minmax(0,1fr)] items-start gap-2 py-2", d < gone && today !== d && "opacity-70")}>
                <span className="pt-2 text-xs leading-tight text-muted-foreground">
                  <span className={cn("block text-sm font-semibold", today === d ? "text-primary" : "text-foreground")}>
                    {name}
                  </span>
                  {today === d ? "today" : date?.getDate()}
                </span>
                <div className="grid gap-1.5">
                  {here.length ? (
                    here.map((s) => (
                      <SessionChip key={s.id} programme={programme} session={s} onClick={() => open2(s)} />
                    ))
                  ) : (
                    <span className="pt-2 text-xs text-muted-foreground/60">Rest</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </DetailSheet>
      <MoveSessionSheet
        open={!!moving && !linking}
        onOpenChange={(next) => !next && setMoving(null)}
        onBack={() => setMoving(null)}
        programme={programme}
        session={moving}
        onPlace={onPlace}
        onSkip={onSkip}
        onCountDone={onLink ? (s) => setLinking(s) : undefined}
      />
      <LinkDoneSheet
        open={!!linking}
        onOpenChange={(next) => {
          if (!next) {
            setLinking(null);
            setMoving(null);
          }
        }}
        onBack={() => setLinking(null)}
        programme={programme}
        slot={linking}
        onLink={onLink}
      />
    </>
  );
}

/**
 * "What are you doing today?" from the Next card: this week's sessions not
 * done yet, or something else. Whatever's picked goes on today; the session
 * planned for today goes back into the week's pool.
 */
export function ChangeTodaySheet({ open, onOpenChange, programme, slot, onPick, onBuild, onFree }) {
  if (!slot) return null;
  const week = weekOf(programme, slot.week_number);
  const options = (week.sessions || []).filter((s) => s.id !== slot.id && isMovable(s));
  return (
    <DetailSheet
      open={open}
      onOpenChange={onOpenChange}
      title="What are you doing today?"
      description={`${slotName(slot)} is planned`}
      showDescription
    >
      {options.length > 0 && (
        <Group title="From this week">
          {options.map((s) => (
            <ItemRow
              key={s.id}
              Icon={CalendarDays}
              title={slotName(s)}
              detail={dayText(programme, { ...s, week_number: s.week_number ?? slot.week_number })}
              onClick={() => onPick(s)}
            />
          ))}
        </Group>
      )}
      <Group title="Something else" footer={`${slotName(slot)} goes back into this week's pool, to place or skip later.`}>
        <ItemRow Icon={Wand2} title="Build a session" detail="Pick a style, like conditioning" onClick={onBuild} />
        <ItemRow Icon={Activity} title="Free training" detail="Log as you go" onClick={onFree} />
      </Group>
    </DetailSheet>
  );
}

// What a slot's kind of day counts toward, in Activity's terms.
const SLOT_TAGS = {
  resistance: "resistance",
  resistance_intervals: "resistance",
  intervals: "anaerobic",
  zone2: "aerobic",
  zone2_sprints: "aerobic",
  vo2max: "aerobic",
};

/** Whether a done session is the same kind of day as the slot. */
export const matchesSlot = (programme, slot, session) => {
  const want = SLOT_TAGS[slotIdentity(programme, slot)?.kind];
  if (!want) return false;
  const tags = readCountsAs(session?.plan_data?.countsAs) ?? adaptationsForWorkoutType(session?.workout_type);
  return Array.isArray(tags) && tags.includes(want);
};

/**
 * Count a session you've already done as this programme session: one you
 * did outside the programme (#196). The person knows what they did, so any
 * session can be picked; the same kind of day is listed first and marked.
 */
export function LinkDoneSheet({ open, onOpenChange, programme, slot, onLink, onBack }) {
  const [sessions, setSessions] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setSessions(null);
    getSessions({ limit: 20 })
      .then((rows) => !cancelled && setSessions(Array.isArray(rows) ? rows : []))
      .catch(() => !cancelled && setSessions([]));
    return () => {
      cancelled = true;
    };
  }, [open]);
  if (!slot) return null;

  const linked = new Set(
    (programme?.weeks || []).flatMap((w) => w.sessions || []).map((s) => s.workout_session_id).filter(Boolean),
  );
  const cutoff = Date.now() - 14 * 86400000;
  const candidates = (sessions || [])
    .filter((s) => !linked.has(s.id) && Date.parse(s.started_at) >= cutoff)
    .map((s) => ({ ...s, match: matchesSlot(programme, slot, s) }))
    .sort((a, b) => Number(b.match) - Number(a.match) || Date.parse(b.started_at) - Date.parse(a.started_at));

  return (
    <DetailSheet
      open={open}
      onOpenChange={onOpenChange}
      onBack={onBack}
      title="Count a session you've done"
      description={`As ${slotName(slot)}`}
      showDescription
    >
      {sessions == null ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {"Loading your sessions\u2026"}
        </p>
      ) : candidates.length === 0 ? (
        <p className="text-sm text-muted-foreground">No sessions in the last two weeks that aren't already in the programme.</p>
      ) : (
        <Group title="Last two weeks" footer="Same kind of day first. Pick any: you know what you did.">
          {candidates.map((s) => (
            <ItemRow
              key={s.id}
              Icon={s.match ? Check : Link2}
              title={s.plan_data?.label || s.workout_type || "Session"}
              detail={[
                new Date(s.started_at).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" }),
                s.duration_minutes ? `${s.duration_minutes} min` : null,
                s.match ? "same kind of day" : null,
              ]
                .filter(Boolean)
                .join(" \u00b7 ")}
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await onLink(slot, s);
                  onOpenChange(false);
                } finally {
                  setBusy(false);
                }
              }}
            />
          ))}
        </Group>
      )}
    </DetailSheet>
  );
}
